import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase/client";
import { useAuth } from "./AuthContext";

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const [destination, setDestination] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      navigate("/sign-in", { replace: true });
      return;
    }
    const client = supabase;
    const returnTo =
      searchParams.get("returnTo") ?? window.sessionStorage.getItem("guiding-stars:auth-return-to");
    window.sessionStorage.removeItem("guiding-stars:auth-return-to");
    const safeReturnTo =
      returnTo?.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/atlas";
    const completeSignIn = async () => {
      const providerError = searchParams.get("error_description") ?? searchParams.get("error");
      if (providerError) {
        navigate(`/sign-in?error=${encodeURIComponent(providerError)}`, { replace: true });
        return;
      }

      const code = searchParams.get("code");
      if (!code) {
        navigate("/sign-in?error=session", { replace: true });
        return;
      }
      const result = await client.auth.exchangeCodeForSession(code);

      if (result.error) {
        navigate(`/sign-in?error=${encodeURIComponent(result.error.message)}`, { replace: true });
        return;
      }
      if (!result.data.session) {
        navigate("/sign-in?error=session", { replace: true });
        return;
      }

      // Do not enter a protected route until AuthProvider has hydrated the
      // matching member profile. This prevents a valid login from being
      // mistaken for a missing profile during the OAuth callback race.
      setDestination(safeReturnTo);
    };
    void completeSignIn();
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!destination || auth.loading) return;
    if (!auth.user) {
      navigate("/sign-in?error=session", { replace: true });
      return;
    }
    if (!auth.profile) {
      navigate("/account/recovery", { replace: true });
      return;
    }
    navigate(destination, { replace: true });
  }, [auth.loading, auth.profile, auth.user, destination, navigate]);

  return (
    <div className="auth-status" role="status">
      <LoaderCircle className="spin" aria-hidden="true" />
      <h1>Opening your Atlas…</h1>
      <p>Securely completing your Google sign-in and loading your Foundation profile.</p>
    </div>
  );
}
